import { useCallback, useRef, useState } from 'react';

import useGoBack from '@/hooks/useGoBack';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';

import ArtGameHeader from '../components/ArtGameHeader';

import { INSTRUMENTS } from './audio/instruments';
import ChromaticMusicGallery from './components/ChromaticMusicGallery';
import useChromaticMusicEngine from './hooks/useChromaticMusicEngine';
import {
  MAX_LAYERS,
  SCALE_LANES,
  SCALES,
  VIEWBOX_HEIGHT,
  VIEWBOX_WIDTH,
} from './constants';
import { buildPath } from './utils';

import './index.css';

export default function ChromaticMusicGame() {
  const t = useTranslations('common.newspaper.art_games.chromatic_music');
  const goBackHandler = useGoBack('/1/newspaper/art-games');
  const {
    svgRef,
    brush,
    brushId,
    setBrushId,
    scale,
    scaleId,
    setScaleId,
    tempo,
    handleTempoChange,
    loopEnabled,
    isPlaying,
    togglePlay,
    toggleLoop,
    echoEnabled,
    setEchoEnabled,
    playbackMode,
    setPlaybackMode,
    quietMode,
    setQuietMode,
    masterVolume,
    setMasterVolume,
    layers,
    visibleLayers,
    activeLayer,
    activeLayerId,
    setActiveLayerId,
    liveStroke,
    recentNotes,
    undoLastStroke,
    clearLayer,
    resetAll,
    saveImage,
    isSaving,
    savedPieces,
    saveToast,
    downloadPiece,
    toggleMute,
    toggleSolo,
    addLayer,
    layerError,
    metronomeBeat,
    metronomePhase,
    getAudioStream, // NEW: get audio stream for recording
  } = useChromaticMusicEngine();

  // --- Recording state/refs ---
  const recordCanvasRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedBlobsRef = useRef([]);
  const paintRafRef = useRef(null);
  // Export lifecycle state (so we can disable buttons & show progress)
  const [exportState, setExportState] = useState('idle'); // 'idle' | 'uploading' | 'processing' | 'error'
  const [exportError, setExportError] = useState('');     // last export error (if any)

  // --- Upload WEBM → backend → poll → return MP4 URL ---
  async function uploadAndGetMp4(blob) {
    setExportError('');
    setExportState('uploading');

    // (a) Ask backend for a job + signed upload URL
    const initPayload = {
      duration_s: 12,                 // matches our fixed record length
      width: VIEWBOX_WIDTH,           // for metadata/validation on server side
      height: VIEWBOX_HEIGHT,
      has_audio: true,
      source: 'chromatic-music@web',  // helpful for metrics
    };

    const { response: initResponse, data: initData } = await UserService.initChromaticMusicExport(initPayload);
    if (!initResponse?.ok || !initData?.upload?.url || !initData?.job_id) {
      const message = initData?.message || t('errors.failed_init_export');
      throw new Error(message);
    }

    // (b) PUT the raw WebM into the signed URL (direct-to-storage)
    const putRes = await fetch(initData.upload.url, {
      method: 'PUT',
      headers: { 'Content-Type': 'video/webm' },
      body: blob,
    });
    if (!putRes.ok) throw new Error(t('errors.failed_upload_media'));

    // (c) Tell the API upload is complete (so it can queue the transcode)
    const { response: completeResponse, data: completeData } = await UserService.completeChromaticMusicExport({ job_id: initData.job_id });
    if (!completeResponse?.ok) {
      const message = completeData?.message || t('errors.failed_finalize_export');
      throw new Error(message);
    }

    // (d) Poll the job until the MP4 is ready
    setExportState('processing');
    for (;;) {
      const { response: statusResponse, data: statusData } = await UserService.getChromaticMusicExportStatus(initData.job_id);
      if (!statusResponse?.ok) {
        const message = statusData?.message || t('errors.failed_check_export');
        throw new Error(message);
      }

      const status = statusData || {};
      if (status.state === 'ready') {
        const mp4Url = status.assets?.mp4_url || status.assets?.mp4Url;
        if (!mp4Url) {
          throw new Error(t('errors.missing_media_url'));
        }
        setExportState('idle');
        return { mp4Url, shareUrl: status.share_url || status.shareUrl };
      }
      if (status.state === 'failed') {
        throw new Error(status.error || t('errors.export_failed'));
      }
      // Poll every 1.5s to avoid hammering the API
      await new Promise(r => setTimeout(r, 1500));
    }
  }

  // Paint the current SVG into the recording canvas
  const startMirroringSvgToCanvas = useCallback(() => {
    const svgEl = document.querySelector('.chromatic-music__canvas');
    const canvas = recordCanvasRef.current;
    if (!svgEl || !canvas) return;
    canvas.width = svgEl.viewBox.baseVal.width;
    canvas.height = svgEl.viewBox.baseVal.height;
    const ctx = canvas.getContext('2d');
    const serializer = new XMLSerializer();
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    const draw = () => {
      const svgString = serializer.serializeToString(svgEl);
      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
      paintRafRef.current = requestAnimationFrame(draw);
    };
    draw();
  }, []);

  const stopMirroring = useCallback(() => {
    if (paintRafRef.current) {
      cancelAnimationFrame(paintRafRef.current);
      paintRafRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async (seconds = 12, fps = 30) => {
    if (isRecording) return;
    if (!isPlaying) togglePlay(); // ensure transport is running
    startMirroringSvgToCanvas();
    const canvas = recordCanvasRef.current;
    const videoStream = canvas.captureStream(fps);
    const audioStream = getAudioStream?.();
    const combined = new window.MediaStream([
      ...videoStream.getVideoTracks(),
      ...(audioStream ? audioStream.getAudioTracks() : []),
    ]);
    const mime =
      window.MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' :
        window.MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus' :
          'video/webm';
    recordedBlobsRef.current = [];
    const mr = new window.MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 4000000 });
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => { if (e.data && e.data.size) recordedBlobsRef.current.push(e.data); };
    mr.onstop = async () => {
      stopMirroring();
      if (isPlaying) togglePlay(); // optional: stop playback after capture
      const blob = new Blob(recordedBlobsRef.current, { type: mime });
      setIsRecording(false);

      try {
        // Preferred path: upload the WebM, wait for backend to transcode, return MP4
        const { mp4Url } = await uploadAndGetMp4(blob);
        const a = document.createElement('a');
        a.href = mp4Url;
        a.download = 'chromatic-music.mp4'; // social-friendly container/codec (H.264/AAC)
        a.click();
      } catch (err) {
        console.error(err);
        setExportError(err.message || t('errors.export_failed'));
        setExportState('error');
        // Fallback: still offer the local WebM so users never lose their take
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chromatic-music.webm';
        a.click();
        URL.revokeObjectURL(url);
      }
    };
    mr.start(100);
    setIsRecording(true);
    setTimeout(() => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }, seconds * 1000);
  }, [getAudioStream, isRecording, startMirroringSvgToCanvas, stopMirroring]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  return (
    <>
      <StaticSeoHead
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath="/newspaper/art-games/chromatic-music"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Newspaper', url: 'https://suarte.art/newspaper' },
          { name: 'Art Games', url: 'https://suarte.art/newspaper/art-games' },
          { name: t('hero.title'), url: 'https://suarte.art/newspaper/art-games/chromatic-music' },
        ]}
      />
      <div className="chromatic-music-page">
        <ArtGameHeader
          className="chromatic-music__header"
          title={t('hero.title')}
          onBack={goBackHandler}
          onShare={() => {
            const shareUrl =
              typeof window !== 'undefined' && window.location?.origin
                ? `${window.location.origin}/newspaper/art-games/chromatic-music`
                : 'https://suarte.art/newspaper/art-games/chromatic-music';

            if (navigator?.share) {
              navigator.share({
                title: t('hero.title'),
                text: t('hero.subtitle'),
                url: shareUrl,
              }).catch(() => {});
              return;
            }

            if (navigator?.clipboard?.writeText) {
              navigator.clipboard.writeText(shareUrl).catch(() => {});
            }
          }}
        />

        <div className="chromatic-music__statusbar">
          <div className="chromatic-music__metronome">
            <div
              className="chromatic-music__metronome-beat"
              data-beat={metronomeBeat}
              style={{ '--phase': metronomePhase }}
            />
            <span>{t('common.bpm', { tempo })}</span>
            <button
              type="button"
              className={`chromatic-music__toggle ${isPlaying ? 'is-active' : ''}`}
              onClick={togglePlay}
              aria-pressed={isPlaying}
            >
              {isPlaying ? t('transport.stop') : t('transport.play')}
            </button>
          </div>
        </div>

        <main className="chromatic-music__content">
          <section className="chromatic-music__canvas-panel">
            <div className="chromatic-music__canvas-header">
              <div className="chromatic-music__chip chromatic-music__chip--scale">
                <span>{t('canvas.scale')}</span>
                <strong>{t(scale.labelKey)}</strong>
              </div>
              <div className={`chromatic-music__chip ${loopEnabled ? 'is-active' : ''}`}>
                <span>{t('canvas.loop_capture')}</span>
                <strong>{loopEnabled ? t('canvas.loop_armed') : t('canvas.loop_off')}</strong>
              </div>
              <div className="chromatic-music__layers-indicator">
                {Array.from({ length: MAX_LAYERS }).map((_, index) => {
                  const layer = layers[index];
                  const isActive = layer && layer.id === activeLayerId;
                  const isFilled = Boolean(layer && layer.strokes.length);
                  const color = isFilled
                    ? INSTRUMENTS.find((entry) => entry.id === (layer.strokes[0]?.brushId ?? brushId))?.color ?? '#1a2331'
                    : '#1a2331';
                  return (
                    <button
                      key={`layer-indicator-${index}`}
                      type="button"
                      className={`chromatic-music__layer-dot ${isActive ? 'is-active' : ''} ${isFilled ? 'is-filled' : ''}`}
                      style={{ '--dot-color': color }}
                      onClick={() => layer && setActiveLayerId(layer.id)}
                      disabled={!layer}
                    >
                      <span className="visually-hidden">{layer ? layer.name : t('canvas.empty_slot')}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="chromatic-music__canvas-wrapper">
              <svg
                ref={svgRef}
                className="chromatic-music__canvas"
                viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
                role="application"
                aria-label={t('canvas.aria')}
              >
                <defs>
                  <linearGradient id="chromatic-music-bg" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(14, 19, 29, 0.94)" />
                    <stop offset="50%" stopColor="rgba(16, 32, 55, 0.96)" />
                    <stop offset="100%" stopColor="rgba(14, 19, 29, 0.94)" />
                  </linearGradient>
                  <filter id="stroke-glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <rect x="0" y="0" width={VIEWBOX_WIDTH} height={VIEWBOX_HEIGHT} fill="url(#chromatic-music-bg)" />
                {Array.from({ length: SCALE_LANES + 1 }).map((_, index) => {
                  const x = (VIEWBOX_WIDTH / SCALE_LANES) * index;
                  return (
                    <line
                      key={`lane-${index}`}
                      x1={x}
                      y1={0}
                      x2={x}
                      y2={VIEWBOX_HEIGHT}
                      className="chromatic-music__lane"
                    />
                  );
                })}
                {Array.from({ length: SCALE_LANES }).map((_, index) => {
                  const x = (VIEWBOX_WIDTH / SCALE_LANES) * index + VIEWBOX_WIDTH / SCALE_LANES / 2;
                  return (
                    <g key={`lane-dots-${index}`}>
                      <circle cx={x} cy={36} r={6} className="chromatic-music__lane-dot" />
                      <circle cx={x} cy={VIEWBOX_HEIGHT - 36} r={6} className="chromatic-music__lane-dot" />
                    </g>
                  );
                })}
                {layers.map((layer) =>
                  layer.strokes.map((stroke) => {
                    const strokeBrush = INSTRUMENTS.find((entry) => entry.id === stroke.brushId) ?? brush;
                    return (
                      <path
                        key={stroke.id}
                        d={stroke.path || buildPath(stroke.points)}
                        className="chromatic-music__stroke"
                        style={{ stroke: strokeBrush.color, filter: 'url(#stroke-glow)' }}
                      />
                    );
                  }),
                )}
                {liveStroke && (
                  <path
                    d={liveStroke.path}
                    className="chromatic-music__stroke chromatic-music__stroke--live"
                    style={{ stroke: brush.color, filter: 'url(#stroke-glow)' }}
                  />
                )}
              </svg>
              {/* Off-screen canvas used only for recording frames */}
              <canvas ref={recordCanvasRef} style={{ display: 'none' }} />
              <div className="chromatic-music__note-palette" aria-live="polite">
                {recentNotes.map((note) => (
                  <span key={note.id} className="chromatic-music__note-chip" style={{ '--chip-color': note.color }}>
                    {note.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="chromatic-music__actions">
              <button type="button" onClick={undoLastStroke} className="chromatic-music__action-btn" disabled={!activeLayer?.strokes.length}>
                {t('actions.undo_stroke')}
              </button>
              <button type="button" onClick={() => clearLayer(activeLayerId)} className="chromatic-music__action-btn" disabled={!activeLayer?.strokes.length}>
                {t('actions.clear_layer')}
              </button>
              <button type="button" onClick={resetAll} className="chromatic-music__action-btn chromatic-music__action-btn--reset">
                {t('actions.reset_all')}
              </button>
              <button
                type="button"
                onClick={saveImage}
                className="chromatic-music__action-btn chromatic-music__action-btn--primary"
                disabled={isSaving}
              >
                {isSaving ? t('actions.saving') : t('actions.save_to_gallery')}
              </button>
              <div className="chromatic-music__recording">
                {!isRecording ? (
                  <button
                    type="button"
                    onClick={() => startRecording(12, 30)}
                    className="chromatic-music__action-btn chromatic-music__action-btn--primary"
                    // Prevent starting a new capture while we’re uploading/processing
                    disabled={exportState === 'uploading' || exportState === 'processing'}
                  >
                    {exportState === 'uploading'
                      ? t('actions.uploading')
                      : exportState === 'processing'
                        ? t('actions.processing')
                        : t('actions.record_video')}
                  </button>
                ) : (
                  <button type="button" onClick={stopRecording} className="chromatic-music__action-btn chromatic-music__action-btn--danger">
                    {t('actions.stop_recording')}
                  </button>
                )}
                {/* Show a gentle error if backend fails; user still got WebM fallback */}
                {exportState === 'error' && (
                  <p className="chromatic-music__warning">{exportError}</p>
                )}
                
              </div>
            </div>
            {layerError && <p className="chromatic-music__warning">{layerError}</p>}
          </section>

          <aside className="chromatic-music__sidebar">
            <ChromaticMusicGallery
              pieces={savedPieces}
              toastMessage={saveToast}
              onDownload={downloadPiece}
            />
            <div className="chromatic-music__panel">
              <h2>{t('sidebar.brush_instruments')}</h2>
              <div className="chromatic-music__brush-grid">
                {INSTRUMENTS.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`chromatic-music__brush ${entry.id === brushId ? 'is-active' : ''}`}
                    onClick={() => setBrushId(entry.id)}
                    style={{ '--brush-color': entry.color, '--brush-accent': entry.accent }}
                  >
                    <span className="chromatic-music__brush-icon" aria-hidden="true">{entry.emoji}</span>
                    <span className="chromatic-music__brush-label">{t(entry.labelKey)}</span>
                    <span className="chromatic-music__brush-description">{t(entry.descriptionKey)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="chromatic-music__panel">
              <h2>{t('sidebar.scale_tempo')}</h2>
              <div className="chromatic-music__scale-select">
                {SCALES.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className={`chromatic-music__scale-chip ${entry.id === scaleId ? 'is-active' : ''}`}
                    onClick={() => setScaleId(entry.id)}
                  >
                    {t(entry.labelKey)}
                  </button>
                ))}
              </div>
              <label className="chromatic-music__tempo-slider">
                <span>{t('sidebar.tempo', { tempo })}</span>
                <input type="range" min="90" max="120" step="1" value={tempo} onChange={handleTempoChange} />
              </label>
              <div className="chromatic-music__scale-select" aria-label={t('sidebar.playback_mode_aria')}>
                <button
                  type="button"
                  className={`chromatic-music__scale-chip ${playbackMode === 'gesture' ? 'is-active' : ''}`}
                  onClick={() => setPlaybackMode('gesture')}
                >
                  {t('sidebar.playback_modes.gesture')}
                </button>
                <button
                  type="button"
                  className={`chromatic-music__scale-chip ${playbackMode === 'scan-lr' ? 'is-active' : ''}`}
                  onClick={() => setPlaybackMode('scan-lr')}
                >
                  {t('sidebar.playback_modes.left_to_right')}
                </button>
                <button
                  type="button"
                  className={`chromatic-music__scale-chip ${playbackMode === 'scan-rl' ? 'is-active' : ''}`}
                  onClick={() => setPlaybackMode('scan-rl')}
                >
                  {t('sidebar.playback_modes.right_to_left')}
                </button>
              </div>
              <button type="button" className={`chromatic-music__toggle ${loopEnabled ? 'is-active' : ''}`} onClick={toggleLoop}>
                {loopEnabled ? t('sidebar.disarm_loop') : t('sidebar.arm_loop')}
              </button>
              <button
                type="button"
                className={`chromatic-music__toggle ${echoEnabled ? 'is-active' : ''}`}
                onClick={() => setEchoEnabled((previous) => !previous)}
              >
                {echoEnabled ? t('sidebar.echo_on') : t('sidebar.echo_off')}
              </button>
            </div>

            <div className="chromatic-music__panel">
              <h2>{t('sidebar.layers')}</h2>
              <div className="chromatic-music__layers">
                {visibleLayers.map((layer) => (
                  <div key={layer.id} className={`chromatic-music__layer ${layer.id === activeLayerId ? 'is-active' : ''}`}>
                    <button type="button" className="chromatic-music__layer-select" onClick={() => setActiveLayerId(layer.id)}>
                      <span className="chromatic-music__layer-indicator" style={{ '--layer-color': layer.color }} />
                      <span className="chromatic-music__layer-name">{layer.name}</span>
                      <span className="chromatic-music__layer-count">{t('sidebar.notes', { count: layer.events.length })}</span>
                      {layer.densityReached && <span className="chromatic-music__layer-flag">{t('sidebar.cap')}</span>}
                    </button>
                    <div className="chromatic-music__layer-controls">
                      <button type="button" onClick={() => toggleMute(layer.id)} className={`chromatic-music__layer-btn ${layer.muted ? 'is-active' : ''}`}>
                        {layer.muted ? t('sidebar.unmute') : t('sidebar.mute')}
                      </button>
                      <button type="button" onClick={() => toggleSolo(layer.id)} className={`chromatic-music__layer-btn ${layer.solo ? 'is-active' : ''}`}>
                        {layer.solo ? t('sidebar.soloing') : t('sidebar.solo')}
                      </button>
                      <button type="button" onClick={() => clearLayer(layer.id)} className="chromatic-music__layer-btn">
                        {t('sidebar.clear')}
                      </button>
                    </div>
                  </div>
                ))}
                {layers.length < MAX_LAYERS && (
                  <button type="button" className="chromatic-music__layer-add" onClick={addLayer}>
                    {t('sidebar.add_layer')}
                  </button>
                )}
              </div>
            </div>

            <div className="chromatic-music__panel">
              <h2>{t('sidebar.output')}</h2>
              <label className="chromatic-music__tempo-slider">
                <span>{t('sidebar.master_volume')}</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={masterVolume}
                  onChange={(event) => setMasterVolume(Number(event.target.value))}
                />
              </label>
              <button
                type="button"
                className={`chromatic-music__toggle ${quietMode ? 'is-active' : ''}`}
                onClick={() => setQuietMode((previous) => !previous)}
              >
                {quietMode ? t('sidebar.quiet_mode_on') : t('sidebar.quiet_mode_off')}
              </button>
              <p className="chromatic-music__assistive">
                {t('sidebar.assistive')}
              </p>
            </div>
          </aside>
        </main>
      </div>
    </>
  );
}
